import * as z from 'zod'

export const registerSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
  // Message comes from the centralized zod error map (zod-i18n.ts), so this schema needs no
  // translation function and can be exported directly.
  termsOfUse: z.literal(true),
})

// Step 1 of the wizard — the company's own details (logo, name, size, sector).
export const companyDetailsSchema = z.object({
  image: z.file().or(z.undefined()).or(z.null()),
  name: z.string().min(1),
  employees: z.number(),
  sector: z.number(),
})

// Step 2 of the wizard — where the company is based.
export const companyLocationSchema = z.object({
  country: z.string().min(1),
  province: z.string().min(1),
  city: z.string().min(1),
  // Optional (no min) so the field renders an "Opcional" marker, like the applicant location step.
  cp: z.string(),
})

export const companyInfoSchema = z.object({
  ...companyDetailsSchema.shape,
  ...companyLocationSchema.shape,
})

// Step 3 of the wizard — who we reach out to.
export const contactInfoSchema = z.object({
  contactFirstname: z.string().min(1),
  contactLastname: z.string().min(1),
  // Required: the company must give a contact phone (shows "*" / "Requerido", not "Optional").
  contactPhone: z.string().min(1),
})

const _schema = z.object({
  ...registerSchema.shape,
  ...companyInfoSchema.shape,
  ...contactInfoSchema.shape,
})

export type InitialData = Omit<Partial<z.input<typeof _schema>>, 'image'> & { image?: string | null }

// The persisted company fields the registration wizard collects across its steps. The company name
// is stored as the user's `firstname`, surfaced as `name`.
export type CompanyUser = {
  name?: string | null
  employees?: number | null
  sector?: number | null
  country?: string | null
  province?: string | null
  city?: string | null
  cp?: string | null
  contactFirstname?: string | null
  contactLastname?: string | null
  contactPhone?: string | null
}

// First wizard step (details → location → contact) whose data doesn't satisfy that step's schema,
// or null when registration is complete. Mirrors firstIncompleteProfileStep and the server's
// fullyRegisteredCompanyConditions, so a half-registered company resumes where it left off.
export function firstIncompleteCompanyStep(user: CompanyUser): 'details' | 'location' | 'contact' | null {
  // `image` is optional in the schema (a saved company has a CDN url, not a File), so omit it.
  if (
    !companyDetailsSchema.safeParse({
      image: undefined,
      name: user.name,
      employees: user.employees,
      sector: user.sector,
    }).success
  )
    return 'details'

  if (
    !companyLocationSchema.safeParse({
      country: user.country,
      province: user.province,
      city: user.city,
      cp: user.cp ?? '',
    }).success
  )
    return 'location'

  if (
    !contactInfoSchema.safeParse({
      contactFirstname: user.contactFirstname,
      contactLastname: user.contactLastname,
      contactPhone: user.contactPhone,
    }).success
  )
    return 'contact'

  return null
}
